import React, { useEffect, useMemo, useState } from 'react';
import { FloatingSidebar } from './ViewComponents';
import { useDispatch, useSelector } from "react-redux";
import {
  loadUsersInOffice,
  MapView,
  selectedOffice,
  selectIsUsersInOfficeLoading,
  selectSelectedMapView,
  selectSelectedOffice,
  setSelectedCarParkSpace
} from "../../../store/officeView.duck";
import { CreateFloorPlanInstructions } from "./CreateFloorPlanInstructions";
import { ChoseOfficeSidebar } from "./ChoseOfficeSidebar";
import {
  selectActiveOffices,
  selectIsAdvanceHotDeskingLoading
} from "@features/desks/store/advanceHotDeskingSetup.duck";
import { SelectOfficeFloor } from "./SelectOfficeFloor";
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { selectActiveDay } from "../../../store/editMovements.duck";
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { WithOfficeChildren } from '@features/desks/hooks/WithOfficeChildren';
import styled from "styled-components";
import { FloorMapHeader } from "./floor-map-view/FloorMapHeader";
import IconButton from '@shared/ui/molecules/icon-button/IconButton';
import { IconTypes } from '@shared/ui/atoms/icon/Icon';
import Layout from '@shared/ui/organisms/layout/Layout';
import { OfficeFloorMap } from "./OfficeFloorMap";
import { loadAllMeetingRoomsForOffice } from "@features/meeting-rooms/pages/models/room-finder.duck";
import { MeetRoomCapacityBands } from "@features/meeting-rooms/pages/models/MeetingRooms";
import { breakpoints, desktopSmall } from '@shared/ui/atoms/MediaQueries';
import { resetManageDesks, selectSidebarMode } from "../ducks/manageDesks.duck";
import { SidebarMode } from "../data/ManageDesks.model";
import { ManageDesksSidebar } from "./ManageDesksSidebar";
import { WithDefaultOfficeSelection } from '@shared/hooks/WithDefaultOfficeSelection';
import { CarParkingMapView } from "./car-parking-map-view/CarParkingMapView";
import { CarParkSpace } from '@features/whereabouts/services/CarParkingPlanService';
import { isInTeams } from "@shared/utils/TeamsUtils";

export function OfficeFloorMapView() {
  const dispatch = useDispatch();
  const isHotDeskingLoading = useSelector(selectIsAdvanceHotDeskingLoading);
  const isUsersInOfficeLoading = useSelector(selectIsUsersInOfficeLoading);
  const selectedOffice = useSelector(selectSelectedOffice);
  const selectedMapView = useSelector(selectSelectedMapView);
  const selectedDate = useSelector(selectActiveDay);
  const sidebarMode = useSelector(selectSidebarMode);
  const [sideBarOpen, setSideBarOpen] = useState(false);


  WithDefaultOfficeSelection(true);

  const isLoading = useMemo(() => isHotDeskingLoading || isUsersInOfficeLoading, [isHotDeskingLoading, isUsersInOfficeLoading]);

  useEffect(() => {
    if (selectedOffice && selectedDate) {
      dispatch(loadUsersInOffice({date: selectedDate, office: selectedOffice}));
    }
  }, [dispatch, selectedDate, selectedOffice]);

  useEffect(() => {
    if (selectedOffice) {
      dispatch(loadAllMeetingRoomsForOffice({office: selectedOffice, capacityBands: MeetRoomCapacityBands.ANY, facilities: []}));
    }
  }, [dispatch, selectedOffice]);

  useEffect(() => {
    return () => {
      dispatch(resetManageDesks());
    }
  }, [dispatch]);

  const onParkingSpaceSelected = (parkingSpace: CarParkSpace) => {
    dispatch(setSelectedCarParkSpace(parkingSpace));
  }

  return (
    <Layout hideFooter={true} fullScreenView={true} style={{maxWidth: '100%'}}>
      <OfficeFloorMapContainer isInTeams={isInTeams()}>
        <Sidebar className={sideBarOpen ? 'officeFloorMap__sidebar officeFloorMap__sidebar--open' : 'officeFloorMap__sidebar'}>
          <IconButton icon={IconTypes.Close} onClick={() => setSideBarOpen(false)} className={'officeFloorMap__sidebar--closeIcon'} />

          {sidebarMode === SidebarMode.ChooseOffice && <ChoseOfficeSidebar />}
          {sidebarMode === SidebarMode.ManageDesks && <ManageDesksSidebar />}
        </Sidebar>
        <ContentContainer>
          <FloorMapHeader onSideBarOpen={(val: boolean) => setSideBarOpen(val)} />
          <div>
            {isLoading && <LoadingSpinner fullScreen={true} />}
            <MapContainer>
              {selectedMapView === MapView.CarPark ? <CarParkingMapView onCarParkingSpaceSelected={onParkingSpaceSelected} /> : <FloorMapContent />}
            </MapContainer>
          </div>
        </ContentContainer>
      </OfficeFloorMapContainer>
    </Layout>
  )
}

function FloorMapContent() {
  const currentOffice = useSelector(selectedOffice);
  const offices = useSelector(selectActiveOffices);
  const isLoading = useSelector(selectIsAdvanceHotDeskingLoading);
  const officeChildren = WithOfficeChildren(currentOffice?.id);

  if (offices.length > 0 && currentOffice?.floorPlanEnabled) {
    if (officeChildren.length > 0 && !currentOffice?.hasAreas) {
      return <SelectOfficeFloor />
    } else {
      return <OfficeFloorMap showAdminButtons={true} />
    }
  } else if (isLoading) {
    return <Center><LoadingSpinner hideBorder={true} hideText={true} /></Center>
  } else if (!isLoading && !!currentOffice && !currentOffice.floorPlanEnabled) {
    return <CreateFloorPlanInstructions/>
  } else {
    return null;
  }
}

const MapContainer = styled.div`
  flex: 1;
  min-width: 400px;
  overflow: auto;
  max-height: 100%;
`

const OfficeFloorMapContainer = styled.div<any>`
  max-height: inherit;
  position: relative;
  display: flex;

  width: 100%;
  margin: 0 auto;
  max-width: ${breakpoints.desktopUberWide}px;
  height: calc(100vh - ${props => props.isInTeams ? '65px' : '114px'});

  .officeFloorMap__header {
    max-width: ${breakpoints.desktopUberWide}px;
  }

  @media (${desktopSmall}) {
    .officeFloorMap__closeBtn {
      display: none !important;
    }
  }

  .officeFloorMap__container {
    overflow: auto;
    height: calc(100vh - (114px + 175px));
    max-width: 100%;
    @media (${desktopSmall}) {
      height: calc(100vh - (114px + 72px));
    }
  }
  .officeFloorMap__sidebar {
    flex: 0 0 360px !important;
    order: 2;
  }
`


const Sidebar = styled(FloatingSidebar)<any>`
  height: 100%;
  position: absolute;
  display: ${props => props.isOpen ? 'block' : 'none'};
  @media (${desktopSmall}) {
    position: relative;
    display: block !important;
  }
  .officeFloorMap__closeBtn {
    top: 16px;
    right: 16px;
    position: absolute;
  }
`

const ContentContainer = styled.div`
  flex: 1 !important;
  order: 1;
  overflow: hidden;
`
